<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useQaPermissions } from 'dashboard/composables/useQaPermissions';
import { confirmQaDestructive } from 'dashboard/composables/useQaConfirm';
import { useQaDrawer } from 'dashboard/composables/useQaDrawer';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import CannedResponseApi from 'dashboard/api/cannedResponse';

// QA_FE_CONFIRM_V1 + QA_FE_DRAWER_V1 + QA_FE_POLICY_I18N_V1
// Template audit incidents intentionally live in the canonical Live Monitor.
const { t } = useI18n();
const store = useStore();
const showAlert = useAlert;
const { accountScopedRoute } = useAccount();
const { canManagePolicies } = useQaPermissions();

const isLoading = ref(false);
const isSaving = ref(false);
const policies = ref([]);
const cannedResponses = ref([]);
const summary = reactive({ rollout: {}, timezone: 'UTC' });
const selectedPolicyId = ref('');
const showPolicyDrawer = ref(false);
const previewCannedId = ref(null);
const cannedSearch = ref('');
const intentTitleTouched = ref(false);
const triggersTouched = ref(false);

const emptyForm = () => ({
  name: '',
  intentTitle: '',
  desiredEnabled: false,
  priority: 100,
  keywordMode: 'any_keyword',
  comparisonMode: 'strict_text',
  triggerKeywordsText: '',
  requiredTermsText: '',
  allowlistPhrasesText: '',
  negativeTermsText: '',
  triggerRegex: '',
  approvedVariablesText: 'agent_name, customer_name, order_id, date, time',
  requireAttachmentMatch: false,
  approvedCannedIds: [],
  customerBurstWindowMinutes: 2,
  replyTurnWindowMinutes: 10,
  noReplyTimeoutMinutes: 5,
  scopeInboxIds: [],
  scopeTeamIds: [],
  scopeChannelTypes: [],
  scheduleTimezone: summary.timezone || 'UTC',
  scheduleWeekdays: [1, 2, 3, 4, 5],
  scheduleWindowsText: '',
  triggersConfirmed: false,
  changeNote: '',
});

const policyForm = reactive(emptyForm());
const selectedPolicy = computed(() =>
  policies.value.find(
    policy => String(policy.id) === String(selectedPolicyId.value)
  )
);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxOptions = computed(() => {
  const options = [...inboxes.value];
  policyForm.scopeInboxIds.forEach(id => {
    if (!options.some(inbox => Number(inbox.id) === Number(id))) {
      options.push({ id, name: `#${id}` });
    }
  });
  return options;
});
const teamOptions = computed(() => {
  const options = [...teams.value];
  policyForm.scopeTeamIds.forEach(id => {
    if (!options.some(team => Number(team.id) === Number(id))) {
      options.push({ id, name: `#${id}` });
    }
  });
  return options;
});
const publicCannedResponses = computed(() =>
  cannedResponses.value.filter(canned => {
    const visibility = canned?.visibility;
    return (
      visibility === 'public_access' ||
      visibility === 0 ||
      visibility === '0' ||
      visibility == null
    );
  })
);
const policyCounts = computed(() => ({
  total: policies.value.length,
  published: policies.value.filter(policy => Number(policy.current_version) > 0)
    .length,
  enabled: policies.value.filter(policy => policy.enabled).length,
  drafts: policies.value.filter(
    policy =>
      Number(policy.current_version) === 0 || policy.has_unpublished_changes
  ).length,
}));
const rolloutStatus = computed(() => {
  const rollout = summary.rollout?.template_audit;
  return typeof rollout === 'string' ? rollout : rollout?.mode || 'unset';
});
const liveMonitorRoute = computed(() =>
  accountScopedRoute(
    'qa_monitor_incidents',
    {},
    { source_type: 'template_audit', tab: 'open' }
  )
);
const channelOptions = computed(() => {
  const values = new Set(policyForm.scopeChannelTypes);
  inboxes.value.forEach(inbox => {
    if (inbox.channel_type) values.add(String(inbox.channel_type));
  });
  return Array.from(values)
    .filter(Boolean)
    .sort()
    .map(value => ({
      value,
      label: String(value).replace(/^Channel::/, ''),
    }));
});
const filteredCannedResponses = computed(() => {
  const query = cannedSearch.value.trim().toLowerCase();
  if (!query) return publicCannedResponses.value;
  return publicCannedResponses.value.filter(canned =>
    `${canned.short_code || ''} ${canned.group_name || ''} ${canned.content || ''}`
      .toLowerCase()
      .includes(query)
  );
});
const selectedCanned = computed(() =>
  publicCannedResponses.value.filter(canned =>
    policyForm.approvedCannedIds.map(Number).includes(Number(canned.id))
  )
);
const previewCanned = computed(() => {
  const id = previewCannedId.value || policyForm.approvedCannedIds[0];
  return (
    publicCannedResponses.value.find(
      canned => Number(canned.id) === Number(id)
    ) || null
  );
});
const snapshotByCannedId = computed(() => {
  const map = new Map();
  (selectedPolicy.value?.approved_canned_snapshots || []).forEach(snapshot => {
    const id = Number(snapshot.canned_response_id || snapshot.cannedResponseId);
    if (id) map.set(id, snapshot);
  });
  return map;
});

const policyDisplayTitle = policy =>
  policy?.intent_title ||
  policy?.name ||
  t('QA_DASHBOARD.TEMPLATE_POLICY.UNTITLED');

const policyCannedLabels = policy => {
  const ids = (policy?.approved_canned_response_ids || []).map(Number);
  if (!ids.length) return [];
  const snapshots = policy?.approved_canned_snapshots || [];
  return ids.map(id => {
    const snap = snapshots.find(
      row => Number(row.canned_response_id || row.cannedResponseId) === id
    );
    if (snap?.short_code) return snap.short_code;
    const live = publicCannedResponses.value.find(c => Number(c.id) === id);
    return live?.short_code || `#${id}`;
  });
};

const policyTriggerChips = (policy, limit = 3) => {
  const triggers = Array.isArray(policy?.trigger_keywords)
    ? policy.trigger_keywords.filter(Boolean)
    : [];
  return {
    shown: triggers.slice(0, limit),
    extra: Math.max(0, triggers.length - limit),
  };
};

const policyCannedChips = (policy, limit = 2) => {
  const labels = policyCannedLabels(policy);
  return {
    shown: labels.slice(0, limit),
    extra: Math.max(0, labels.length - limit),
  };
};

const fmtShortDate = value => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formTriggerPreview = computed(() => {
  const triggers = splitCsv(policyForm.triggerKeywordsText);
  if (!triggers.length)
    return t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_NO_TRIGGERS');
  const shown = triggers.slice(0, 4);
  const extra = triggers.length - shown.length;
  return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
});

const formCannedPreview = computed(() => {
  const labels = selectedCanned.value.map(
    canned => canned.short_code || `#${canned.id}`
  );
  if (!labels.length) return t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_NO_CANNED');
  return labels.join(', ');
});

const formPublishState = computed(() => {
  if (!selectedPolicy.value) {
    return t('QA_DASHBOARD.TEMPLATE_POLICY.STATUS_DRAFT_ONLY');
  }
  return policyLifecycle(selectedPolicy.value);
});

const closePolicyDrawer = () => {
  showPolicyDrawer.value = false;
};
const { panelRef: policyDrawerPanelRef } = useQaDrawer({
  open: showPolicyDrawer,
  onClose: closePolicyDrawer,
});

const splitCsv = value =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const DEFAULT_VARIABLE_PATTERNS = {
  agent_name: '\\{\\{\\s*agent[_ ]?name\\s*\\}\\}',
  customer_name: '\\{\\{\\s*customer[_ ]?name\\s*\\}\\}',
  contact_name: '\\{\\{\\s*contact[_ ]?name\\s*\\}\\}',
  order_id: '\\b(?:order[#:\\s-]*)?[A-Z]{0,3}\\d{5,}\\b',
  phone: '\\+?\\d[\\d\\s\\-()]{7,}\\d',
  email: '[A-Z0-9._%+\\-]+@[A-Z0-9.\\-]+\\.[A-Z]{2,}',
  date: '\\b\\d{1,2}[\\/\\-]\\d{1,2}(?:[\\/\\-]\\d{2,4})?\\b',
  time: '\\b\\d{1,2}:\\d{2}(?:\\s?[ap]m)?\\b',
};

const parseApprovedVariables = value =>
  splitCsv(value).map(name => ({
    name,
    pattern: DEFAULT_VARIABLE_PATTERNS[name] || `\\{\\{\\s*${name}\\s*\\}\\}`,
  }));

const parseScheduleWindows = value =>
  splitCsv(value)
    .map(item => {
      const [start, end] = item.split('-').map(part => part.trim());
      return start && end ? { start, end } : null;
    })
    .filter(Boolean);

const secondsToMinutes = (value, fallback) => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds / 60 : fallback;
};

const minutesToSeconds = (value, fallback) => {
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0
    ? Math.round(minutes * 60)
    : fallback;
};

const resetPolicyForm = () => {
  selectedPolicyId.value = '';
  Object.assign(policyForm, emptyForm());
  previewCannedId.value = null;
  cannedSearch.value = '';
  intentTitleTouched.value = false;
  triggersTouched.value = false;
};

const fillPolicyForm = policy => {
  if (!policy) return;
  const schedule = policy.schedule || {};
  Object.assign(policyForm, {
    name: policy.name || '',
    intentTitle: policy.intent_title || '',
    desiredEnabled: Boolean(policy.enabled),
    priority: Number(policy.priority || 100),
    keywordMode: policy.keyword_mode || 'any_keyword',
    comparisonMode: policy.comparison_mode || 'strict_text',
    triggerKeywordsText: (policy.trigger_keywords || []).join(', '),
    requiredTermsText: (policy.required_terms || []).join(', '),
    allowlistPhrasesText: (policy.allowlist_phrases || []).join(', '),
    negativeTermsText: (policy.negative_terms || []).join(', '),
    triggerRegex: policy.trigger_regex || '',
    approvedVariablesText:
      (policy.approved_variables || [])
        .map(item => item.name || item)
        .filter(Boolean)
        .join(', ') || emptyForm().approvedVariablesText,
    requireAttachmentMatch: Boolean(policy.require_attachment_match),
    approvedCannedIds: (policy.approved_canned_response_ids || []).map(Number),
    customerBurstWindowMinutes: secondsToMinutes(
      policy.customer_burst_window_seconds,
      2
    ),
    replyTurnWindowMinutes: secondsToMinutes(
      policy.reply_turn_window_seconds,
      10
    ),
    noReplyTimeoutMinutes: secondsToMinutes(policy.no_reply_timeout_seconds, 5),
    scopeInboxIds: (policy.scope_inbox_ids || []).map(Number),
    scopeTeamIds: (policy.scope_team_ids || []).map(Number),
    scopeChannelTypes: (policy.scope_channel_types || []).map(String),
    scheduleTimezone: schedule.timezone || summary.timezone || 'UTC',
    scheduleWeekdays:
      Array.isArray(schedule.weekdays) && schedule.weekdays.length
        ? schedule.weekdays.map(Number)
        : [],
    scheduleWindowsText: Array.isArray(schedule.time_windows)
      ? schedule.time_windows
          .map(window => `${window.start}-${window.end}`)
          .join(', ')
      : '',
    triggersConfirmed: Boolean((policy.trigger_keywords || []).length),
    changeNote: '',
  });
  previewCannedId.value = policyForm.approvedCannedIds[0] || null;
  intentTitleTouched.value = Boolean(policy.intent_title);
  triggersTouched.value = Boolean((policy.trigger_keywords || []).length);
};

watch(selectedPolicyId, () => {
  if (selectedPolicy.value) fillPolicyForm(selectedPolicy.value);
});

const loadPage = async () => {
  isLoading.value = true;
  try {
    const [policyResult, cannedResult, summaryResult] =
      await Promise.allSettled([
        QaDashboardApi.getTemplatePolicies(),
        CannedResponseApi.get({ searchKey: '' }),
        QaDashboardApi.getSummary({ days: 14 }),
      ]);
    if (policyResult.status === 'fulfilled') {
      policies.value = policyResult.value?.data?.items || [];
    }
    if (cannedResult.status === 'fulfilled') {
      cannedResponses.value = Array.isArray(cannedResult.value?.data)
        ? cannedResult.value.data
        : [];
    }
    if (summaryResult.status === 'fulfilled') {
      const item = summaryResult.value?.data?.item || {};
      summary.rollout = item.rollout || {};
      summary.timezone = item.timezone || 'UTC';
      if (!selectedPolicyId.value) {
        policyForm.scheduleTimezone = summary.timezone;
      }
    }
    const failedResult = [policyResult, cannedResult, summaryResult].find(
      result => result.status === 'rejected'
    );
    if (failedResult) {
      const error = failedResult.reason;
      showAlert(
        error?.response?.data?.error ||
          t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_LOAD_POLICIES')
      );
    }
    if (selectedPolicyId.value) {
      const refreshed = policies.value.find(
        policy => String(policy.id) === String(selectedPolicyId.value)
      );
      if (refreshed) fillPolicyForm(refreshed);
      else resetPolicyForm();
    }
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_LOAD_POLICIES')
    );
  } finally {
    isLoading.value = false;
  }
};

const ensureScopeStores = async () => {
  const tasks = [];
  if (!inboxes.value.length) tasks.push(store.dispatch('inboxes/get'));
  if (!teams.value.length) tasks.push(store.dispatch('teams/get'));
  try {
    await Promise.all(tasks);
  } catch (error) {
    // Scope IDs remain visible from policy data if reference stores are unavailable.
  }
};

const buildPolicyPayload = () => {
  const replyTurnSeconds = minutesToSeconds(
    policyForm.replyTurnWindowMinutes,
    600
  );
  const noReplySeconds = Math.min(
    minutesToSeconds(policyForm.noReplyTimeoutMinutes, 300),
    replyTurnSeconds
  );
  return {
    name: policyForm.name.trim(),
    intent_title: policyForm.intentTitle.trim(),
    // Draft writes never change live enablement. Publish applies desiredEnabled.
    enabled: selectedPolicy.value?.enabled || false,
    priority: Number(policyForm.priority) || 100,
    keyword_mode: policyForm.keywordMode,
    comparison_mode: policyForm.comparisonMode,
    trigger_keywords: splitCsv(policyForm.triggerKeywordsText),
    required_terms:
      policyForm.comparisonMode === 'contains_terms'
        ? splitCsv(policyForm.requiredTermsText)
        : [],
    allowlist_phrases: splitCsv(policyForm.allowlistPhrasesText),
    negative_terms: splitCsv(policyForm.negativeTermsText),
    trigger_regex: String(policyForm.triggerRegex || '').trim(),
    approved_variables: parseApprovedVariables(
      policyForm.approvedVariablesText
    ),
    require_attachment_match: Boolean(policyForm.requireAttachmentMatch),
    approved_canned_response_ids: policyForm.approvedCannedIds.map(Number),
    customer_burst_window_seconds: minutesToSeconds(
      policyForm.customerBurstWindowMinutes,
      120
    ),
    reply_turn_window_seconds: replyTurnSeconds,
    no_reply_timeout_seconds: noReplySeconds,
    scope_inbox_ids: policyForm.scopeInboxIds.map(Number),
    scope_team_ids: policyForm.scopeTeamIds.map(Number),
    scope_channel_types: policyForm.scopeChannelTypes,
    schedule: {
      timezone: policyForm.scheduleTimezone || 'UTC',
      weekdays: policyForm.scheduleWeekdays.map(Number),
      time_windows: parseScheduleWindows(policyForm.scheduleWindowsText),
    },
  };
};

const validatePayload = payload => {
  if (!payload.name) return 'ERROR_NAME_REQUIRED';
  if (!payload.intent_title) return 'ERROR_INTENT_REQUIRED';
  if (!payload.trigger_keywords.length) return 'ERROR_TRIGGER_REQUIRED';
  if (!policyForm.triggersConfirmed) return 'ERROR_CONFIRM_TRIGGERS';
  if (!payload.approved_canned_response_ids.length)
    return 'ERROR_SELECT_CANNED';
  if (
    payload.comparison_mode === 'contains_terms' &&
    !payload.required_terms.length
  ) {
    return 'ERROR_REQUIRED_TERMS';
  }
  return '';
};

const savePolicy = async ({ quiet = false } = {}) => {
  const payload = buildPolicyPayload();
  const errorKey = validatePayload(payload);
  if (errorKey) {
    showAlert(t(`QA_DASHBOARD.TEMPLATE_POLICY.${errorKey}`));
    return false;
  }

  isSaving.value = true;
  try {
    let policyId = selectedPolicy.value?.id;
    if (policyId) {
      await QaDashboardApi.updateTemplatePolicy(policyId, payload);
    } else {
      const response = await QaDashboardApi.createTemplatePolicy({
        ...payload,
        enabled: false,
      });
      policyId = response?.data?.item?.id;
      selectedPolicyId.value = String(policyId || '');
      policyForm.desiredEnabled = false;
    }
    await loadPage();
    if (!quiet) {
      showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.SUCCESS_DRAFT_SAVED'));
    }
    return policyId || false;
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_SAVE')
    );
    return false;
  } finally {
    isSaving.value = false;
  }
};

const publishPolicy = async () => {
  const policy = selectedPolicy.value;
  if (!policy) {
    showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_SAVE_BEFORE_PUBLISH'));
    return;
  }
  if (
    !confirmQaDestructive({
      action: t('QA_DASHBOARD.TEMPLATE_POLICY.CONFIRM_PUBLISH_ACTION'),
      subject: policy.name || `#${policy.id}`,
    })
  ) {
    return;
  }

  const enabledAfterPublish = policyForm.desiredEnabled;
  const publishNote = policyForm.changeNote.trim();
  const policyId = await savePolicy({ quiet: true });
  if (!policyId) return;
  isSaving.value = true;
  try {
    await QaDashboardApi.publishTemplatePolicy(policyId, {
      enabled: enabledAfterPublish,
      change_note:
        publishNote || t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_NOTE_DEFAULT'),
    });
    await loadPage();
    showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.SUCCESS_PUBLISHED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_PUBLISH')
    );
  } finally {
    isSaving.value = false;
  }
};

const syncCannedDraft = async () => {
  if (!selectedPolicy.value) return;
  try {
    await QaDashboardApi.syncTemplatePolicyCanned(selectedPolicy.value.id);
    await loadPage();
    showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.SUCCESS_CANNED_SYNCED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_SYNC_CANNED')
    );
  }
};

const rollbackPolicy = async () => {
  const currentVersion = Number(selectedPolicy.value?.current_version);
  const target = currentVersion - 1;
  if (target < 1) {
    showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_NO_PRIOR_VERSION'));
    return;
  }
  if (
    !confirmQaDestructive({
      action: t('QA_DASHBOARD.TEMPLATE_POLICY.CONFIRM_ROLLBACK_ACTION'),
      subject: selectedPolicy.value.name || `#${selectedPolicy.value.id}`,
      detail: t('QA_DASHBOARD.TEMPLATE_POLICY.CONFIRM_ROLLBACK_DETAIL', {
        version: target,
      }),
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.rollbackTemplatePolicy(selectedPolicy.value.id, {
      version_number: target,
    });
    await loadPage();
    showAlert(
      t('QA_DASHBOARD.TEMPLATE_POLICY.SUCCESS_ROLLBACK', { version: target })
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_ROLLBACK')
    );
  }
};

const deletePolicy = async () => {
  if (!selectedPolicy.value) return;
  if (
    !confirmQaDestructive({
      action: t('QA_DASHBOARD.TEMPLATE_POLICY.CONFIRM_DELETE_ACTION'),
      subject: selectedPolicy.value.name || `#${selectedPolicy.value.id}`,
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.deleteTemplatePolicy(selectedPolicy.value.id);
    resetPolicyForm();
    await loadPage();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_DELETE')
    );
  }
};

const importLegacyTemplates = async () => {
  isSaving.value = true;
  try {
    const response = await QaDashboardApi.importTemplatePoliciesFromLegacy();
    const imported = response?.data?.imported?.length || 0;
    const skipped = response?.data?.skipped?.length || 0;
    const errors = response?.data?.errors?.length || 0;
    await loadPage();
    showAlert(
      t('QA_DASHBOARD.TEMPLATE_POLICY.LEGACY_IMPORT_RESULT', {
        imported,
        skipped,
        errors,
      })
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_IMPORT_LEGACY')
    );
  } finally {
    isSaving.value = false;
  }
};

const cannedOutOfSync = canned => {
  const snapshot = snapshotByCannedId.value.get(Number(canned.id));
  const snapshotAt = snapshot?.canned_updated_at || snapshot?.cannedUpdatedAt;
  if (!snapshotAt || !canned.updated_at) return false;
  return new Date(canned.updated_at).getTime() > new Date(snapshotAt).getTime();
};

const cannedAttachmentCount = canned =>
  Array.isArray(canned?.media_attachments)
    ? canned.media_attachments.length
    : Number(canned?.media_attachment_count || 0);

const formatCannedUpdatedAt = canned => {
  if (!canned?.updated_at)
    return t('QA_DASHBOARD.TEMPLATE_POLICY.NOT_AVAILABLE');
  const date = new Date(canned.updated_at);
  return Number.isNaN(date.getTime())
    ? String(canned.updated_at)
    : date.toLocaleString();
};

const cannedOptionLabel = canned =>
  t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_OPTION', {
    code: canned.short_code || t('QA_DASHBOARD.TEMPLATE_POLICY.NOT_AVAILABLE'),
    group:
      canned.group_name || t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_GENERAL'),
  });

const cannedOptionMeta = canned =>
  t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_META', {
    updated: formatCannedUpdatedAt(canned),
    attachments: cannedAttachmentCount(canned),
  });

const applyCannedPrefill = canned => {
  const shortCode = String(canned?.short_code || '').trim();
  if (!shortCode) return;
  if (!intentTitleTouched.value || !policyForm.intentTitle.trim()) {
    policyForm.intentTitle = shortCode;
  }
  // Do NOT prefill trigger keywords with the canned short_code: that is the
  // AGENT's insert shortcut (e.g. "/1"), not what the CUSTOMER types. Leaving it
  // blank forces the author to enter real customer phrases; validation + the
  // confirm checkbox still gate publishing.
  if (!triggersTouched.value) {
    policyForm.triggersConfirmed = false;
  }
};

const removeCannedId = cannedId => {
  policyForm.approvedCannedIds = policyForm.approvedCannedIds.filter(
    id => Number(id) !== Number(cannedId)
  );
  if (Number(previewCannedId.value) === Number(cannedId)) {
    previewCannedId.value = policyForm.approvedCannedIds[0] || null;
  }
};

const toggleCannedId = cannedId => {
  const id = Number(cannedId);
  if (policyForm.approvedCannedIds.map(Number).includes(id)) {
    removeCannedId(id);
    return;
  }
  const canned = publicCannedResponses.value.find(
    item => Number(item.id) === id
  );
  if (!canned) {
    showAlert(t('QA_DASHBOARD.TEMPLATE_POLICY.ERROR_PUBLIC_CANNED_ONLY'));
    return;
  }
  policyForm.approvedCannedIds = [...policyForm.approvedCannedIds, id];
  previewCannedId.value = id;
  applyCannedPrefill(canned);
};

const policyLifecycle = policy => {
  if (!Number(policy.current_version)) {
    return t('QA_DASHBOARD.TEMPLATE_POLICY.STATUS_DRAFT_ONLY');
  }
  if (policy.has_unpublished_changes) {
    return t('QA_DASHBOARD.TEMPLATE_POLICY.STATUS_PUBLISHED_WITH_DRAFT', {
      version: policy.current_version,
    });
  }
  return t('QA_DASHBOARD.TEMPLATE_POLICY.STATUS_PUBLISHED', {
    version: policy.current_version,
  });
};

const openPolicyDrawer = (policyId = '') => {
  if (policyId) {
    selectedPolicyId.value = String(policyId);
    fillPolicyForm(
      policies.value.find(policy => String(policy.id) === String(policyId))
    );
  } else {
    resetPolicyForm();
  }
  showPolicyDrawer.value = true;
};

onMounted(async () => {
  await Promise.all([loadPage(), ensureScopeStores()]);
});
</script>

<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.PAGE_TITLE') }}
          </h1>
        </div>
        <div class="qa-header-actions">
          <RouterLink
            class="qa-btn qa-btn--primary"
            data-test="live-monitor-link"
            :to="liveMonitorRoute"
          >
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.OPEN_LIVE_MONITOR') }}
          </RouterLink>
          <RouterLink class="qa-btn" :to="accountScopedRoute('canned_list')">
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.MANAGE_CANNED') }}
          </RouterLink>
          <button
            v-if="canManagePolicies"
            type="button"
            class="qa-btn"
            :disabled="isSaving"
            @click="importLegacyTemplates"
          >
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.IMPORT_LEGACY') }}
          </button>
          <button
            v-if="canManagePolicies"
            type="button"
            class="qa-btn"
            @click="openPolicyDrawer()"
          >
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.NEW_POLICY') }}
          </button>
        </div>
      </div>

      <div class="qa-subheader">
        <p class="qa-desc">{{ t('QA_DASHBOARD.TEMPLATE_POLICY.PAGE_DESC') }}</p>
        <p class="qa-hint">
          {{ t('QA_DASHBOARD.TEMPLATE_POLICY.LIVE_ONLY_HINT') }}
        </p>
      </div>

      <div class="qa-content">
        <section class="qa-meta-strip" :aria-busy="isLoading">
          <span
            class="qa-badge"
            :class="`qa-badge--${rolloutStatus === 'on' || rolloutStatus === 'shadow' ? 'info' : 'muted'}`"
          >
            {{
              t(
                `QA_DASHBOARD.TEMPLATE_POLICY.ROLLOUT_${rolloutStatus.toUpperCase()}`
              )
            }}
          </span>
          <span>
            <strong>{{ policyCounts.enabled }}</strong>
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COUNT_ENABLED') }}
          </span>
          <span>
            <strong>{{ policyCounts.published }}</strong>
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COUNT_PUBLISHED') }}
          </span>
          <span>
            <strong>{{ publicCannedResponses.length }}</strong>
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COUNT_CANNED_LIBRARY') }}
          </span>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <div>
              <h2 class="qa-card-title">
                {{ t('QA_DASHBOARD.TEMPLATE_POLICY.POLICIES_TITLE') }}
              </h2>
              <p class="qa-hint">
                {{ t('QA_DASHBOARD.TEMPLATE_POLICY.POLICIES_HINT') }}
              </p>
            </div>
            <button
              type="button"
              class="qa-btn qa-btn--sm"
              :disabled="isLoading"
              @click="loadPage"
            >
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.REFRESH') }}
            </button>
          </div>
          <div class="qa-card-body">
            <div v-if="isLoading" class="qa-empty">
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.LOADING') }}
            </div>
            <div
              v-else-if="!policies.length"
              class="qa-empty qa-empty--onboarding"
            >
              <h3>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.EMPTY_TITLE') }}</h3>
              <p>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.EMPTY_DESC') }}</p>
              <button
                v-if="canManagePolicies"
                type="button"
                class="qa-btn qa-btn--primary"
                @click="openPolicyDrawer()"
              >
                {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CREATE_FIRST') }}
              </button>
            </div>
            <div v-else class="qa-policy-grid">
              <article
                v-for="policy in policies"
                :key="policy.id"
                class="qa-policy-card"
                :role="canManagePolicies ? 'button' : undefined"
                :tabindex="canManagePolicies ? 0 : undefined"
                @click="canManagePolicies && openPolicyDrawer(policy.id)"
                @keydown.enter.prevent="
                  canManagePolicies && openPolicyDrawer(policy.id)
                "
              >
                <div class="qa-policy-card-head">
                  <span
                    class="qa-policy-card-title"
                    :title="policyDisplayTitle(policy)"
                  >
                    {{ policyDisplayTitle(policy) }}
                  </span>
                  <span
                    class="qa-pill"
                    :class="policy.enabled ? 'qa-pill--good' : 'qa-pill--muted'"
                  >
                    {{
                      policy.enabled
                        ? t('QA_DASHBOARD.TEMPLATE_POLICY.ENABLED')
                        : t('QA_DASHBOARD.TEMPLATE_POLICY.DISABLED')
                    }}
                  </span>
                </div>
                <div class="qa-policy-card-recipe">
                  <div class="qa-recipe-line">
                    <span class="qa-recipe-label">
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_WHEN') }}
                    </span>
                    <template v-if="policyTriggerChips(policy).shown.length">
                      <span
                        v-for="trigger in policyTriggerChips(policy).shown"
                        :key="trigger"
                        class="qa-chip qa-chip--trigger"
                      >
                        {{ trigger }}
                      </span>
                      <span
                        v-if="policyTriggerChips(policy).extra"
                        class="qa-chip qa-chip--more"
                      >
                        +{{ policyTriggerChips(policy).extra }}
                      </span>
                    </template>
                    <span v-else class="qa-hint">
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_NO_TRIGGERS') }}
                    </span>
                  </div>
                  <div class="qa-recipe-line">
                    <span class="qa-recipe-label">
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_USE') }}
                    </span>
                    <template v-if="policyCannedChips(policy).shown.length">
                      <span
                        v-for="label in policyCannedChips(policy).shown"
                        :key="label"
                        class="qa-chip qa-chip--canned"
                      >
                        {{ label }}
                      </span>
                      <span
                        v-if="policyCannedChips(policy).extra"
                        class="qa-chip qa-chip--more"
                      >
                        +{{ policyCannedChips(policy).extra }}
                      </span>
                    </template>
                    <span v-else class="qa-hint">
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_NO_CANNED') }}
                    </span>
                  </div>
                </div>
                <div class="qa-policy-card-meta">
                  <span>
                    {{ policyLifecycle(policy) }}
                    <span
                      v-if="policy.needs_attention"
                      class="qa-pill qa-pill--warn"
                      style="margin-left: 6px"
                    >
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.NEEDS_ATTENTION') }}
                    </span>
                  </span>
                  <span class="qa-policy-card-meta-right">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.UPDATED') }}
                    {{ fmtShortDate(policy.updated_at) }}
                    <button
                      v-if="canManagePolicies"
                      type="button"
                      class="qa-btn qa-btn--sm"
                      @click.stop="openPolicyDrawer(policy.id)"
                    >
                      {{ t('QA_DASHBOARD.TEMPLATE_POLICY.EDIT') }}
                    </button>
                  </span>
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div
      v-if="showPolicyDrawer"
      class="qa-drawer-backdrop"
      role="presentation"
      @click.self="closePolicyDrawer"
    >
      <aside
        ref="policyDrawerPanelRef"
        class="qa-drawer"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-label="t('QA_DASHBOARD.TEMPLATE_POLICY.TITLE')"
      >
        <div class="qa-drawer-header">
          <div>
            <h2 class="qa-card-title">
              {{
                selectedPolicy
                  ? t('QA_DASHBOARD.TEMPLATE_POLICY.EDIT_POLICY')
                  : t('QA_DASHBOARD.TEMPLATE_POLICY.NEW_POLICY')
              }}
            </h2>
            <p class="qa-hint">
              {{
                selectedPolicy
                  ? policyLifecycle(selectedPolicy)
                  : t('QA_DASHBOARD.TEMPLATE_POLICY.NEW_DRAFT_HINT')
              }}
            </p>
          </div>
          <button
            type="button"
            class="qa-btn qa-btn--sm"
            @click="closePolicyDrawer"
          >
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CLOSE') }}
          </button>
        </div>

        <div class="qa-drawer-body">
          <section class="qa-recipe-strip" aria-label="Policy recipe">
            <div>
              <span class="qa-label">{{
                t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_WHEN')
              }}</span>
              <strong>{{ formTriggerPreview }}</strong>
            </div>
            <span class="qa-policy-recipe-arrow" aria-hidden="true">→</span>
            <div>
              <span class="qa-label">{{
                t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_USE')
              }}</span>
              <strong>{{ formCannedPreview }}</strong>
            </div>
            <div>
              <span class="qa-label">{{
                t('QA_DASHBOARD.TEMPLATE_POLICY.RECIPE_STATE')
              }}</span>
              <strong>{{ formPublishState }}</strong>
            </div>
          </section>
          <p class="qa-hint">
            {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_SECTION_HINT') }}
          </p>

          <section class="qa-form-section">
            <h3>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.SECTION_BASICS') }}</h3>
            <div class="qa-form-grid">
              <label class="qa-field qa-field--span-2">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.SAVED_POLICIES')
                }}</span>
                <select
                  v-model="selectedPolicyId"
                  class="qa-select"
                  :disabled="isLoading"
                >
                  <option value="">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CREATE_NEW_OPTION') }}
                  </option>
                  <option
                    v-for="policy in policies"
                    :key="policy.id"
                    :value="String(policy.id)"
                  >
                    {{ policy.name }} · {{ policyLifecycle(policy) }}
                  </option>
                </select>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.POLICY_NAME')
                }}</span>
                <input
                  v-model="policyForm.name"
                  class="qa-input"
                  data-test="policy-name"
                  :placeholder="
                    t('QA_DASHBOARD.TEMPLATE_POLICY.PH_POLICY_NAME')
                  "
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.INTENT_TITLE')
                }}</span>
                <input
                  v-model="policyForm.intentTitle"
                  class="qa-input"
                  data-test="policy-intent"
                  :placeholder="t('QA_DASHBOARD.TEMPLATE_POLICY.PH_INTENT')"
                  @input="intentTitleTouched = true"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.KEYWORD_MODE')
                }}</span>
                <select v-model="policyForm.keywordMode" class="qa-select">
                  <option value="any_keyword">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.KEYWORD_ANY') }}
                  </option>
                  <option value="all_keywords">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.KEYWORD_ALL') }}
                  </option>
                </select>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.COMPARISON_MODE')
                }}</span>
                <select
                  v-model="policyForm.comparisonMode"
                  class="qa-select"
                  data-test="comparison-mode"
                >
                  <option value="strict_text">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COMPARE_STRICT') }}
                  </option>
                  <option value="contains_terms">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COMPARE_CONTAINS') }}
                  </option>
                  <option value="semantic_equivalence">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.COMPARE_SEMANTIC') }}
                  </option>
                </select>
                <span
                  v-if="policyForm.comparisonMode === 'semantic_equivalence'"
                  class="qa-hint"
                >
                  {{ t('QA_DASHBOARD.TEMPLATE_POLICY.SEMANTIC_HINT') }}
                </span>
              </label>
              <label class="qa-field qa-field--span-2">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.TRIGGER_KEYWORDS')
                }}</span>
                <textarea
                  v-model="policyForm.triggerKeywordsText"
                  class="qa-textarea"
                  data-test="trigger-keywords"
                  rows="2"
                  @input="
                    triggersTouched = true;
                    policyForm.triggersConfirmed = false;
                  "
                />
                <span class="qa-checkline">
                  <input
                    v-model="policyForm.triggersConfirmed"
                    data-test="confirm-triggers"
                    type="checkbox"
                  />
                  {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CONFIRM_TRIGGERS') }}
                </span>
              </label>
              <label
                v-if="policyForm.comparisonMode === 'contains_terms'"
                class="qa-field qa-field--span-2"
              >
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.REQUIRED_TERMS')
                }}</span>
                <textarea
                  v-model="policyForm.requiredTermsText"
                  class="qa-textarea"
                  data-test="required-terms"
                  rows="2"
                />
              </label>
            </div>
          </section>

          <section class="qa-form-section">
            <div class="qa-section-heading">
              <div>
                <h3>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.SECTION_CANNED') }}</h3>
                <p class="qa-hint">
                  {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_SECTION_HINT') }}
                </p>
              </div>
              <RouterLink
                class="qa-link"
                :to="accountScopedRoute('canned_list')"
              >
                {{ t('QA_DASHBOARD.TEMPLATE_POLICY.MANAGE_CANNED') }}
              </RouterLink>
            </div>
            <input
              v-model="cannedSearch"
              class="qa-input"
              :placeholder="t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_SEARCH')"
            />
            <div v-if="selectedCanned.length" class="qa-canned-chips">
              <button
                v-for="canned in selectedCanned"
                :key="`chip-${canned.id}`"
                type="button"
                class="qa-chip"
                :class="{
                  'qa-chip--active':
                    Number(previewCannedId) === Number(canned.id),
                  'qa-chip--warn': cannedOutOfSync(canned),
                }"
                @click="previewCannedId = canned.id"
              >
                {{ canned.short_code || canned.id }}
                <span v-if="cannedOutOfSync(canned)">
                  · {{ t('QA_DASHBOARD.TEMPLATE_POLICY.FLAG_OUT_OF_SYNC') }}
                </span>
                <span class="qa-chip-x" @click.stop="removeCannedId(canned.id)"
                  >×</span
                >
              </button>
            </div>
            <div class="qa-canned-grid">
              <div class="qa-canned-picker">
                <button
                  v-for="canned in filteredCannedResponses"
                  :key="canned.id"
                  type="button"
                  class="qa-canned-option"
                  :data-test="`canned-option-${canned.id}`"
                  :class="{
                    'qa-canned-option--selected': policyForm.approvedCannedIds
                      .map(Number)
                      .includes(Number(canned.id)),
                  }"
                  @click="toggleCannedId(canned.id)"
                  @mouseenter="previewCannedId = canned.id"
                >
                  <strong>{{ cannedOptionLabel(canned) }}</strong>
                  <span>{{ cannedOptionMeta(canned) }}</span>
                  <small>{{
                    String(canned.content || '').slice(0, 100) ||
                    t('QA_DASHBOARD.TEMPLATE_POLICY.MEDIA_ONLY')
                  }}</small>
                </button>
                <div
                  v-if="!filteredCannedResponses.length"
                  class="qa-canned-empty"
                >
                  {{ t('QA_DASHBOARD.TEMPLATE_POLICY.CANNED_EMPTY') }}
                </div>
              </div>
              <div class="qa-canned-preview">
                <strong>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.PREVIEW') }}</strong>
                <pre>{{
                  previewCanned?.content ||
                  t('QA_DASHBOARD.TEMPLATE_POLICY.PREVIEW_EMPTY')
                }}</pre>
              </div>
            </div>
          </section>

          <details class="qa-form-section qa-advanced">
            <summary>
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.SECTION_SCOPE_TIMING') }}
            </summary>
            <p class="qa-hint">
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.SCOPE_HINT') }}
            </p>
            <div class="qa-form-grid">
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.INBOXES')
                }}</span>
                <select
                  v-model="policyForm.scopeInboxIds"
                  class="qa-select qa-select--multi"
                  multiple
                >
                  <option
                    v-for="inbox in inboxOptions"
                    :key="inbox.id"
                    :value="Number(inbox.id)"
                  >
                    {{ inbox.name || `#${inbox.id}` }}
                  </option>
                </select>
                <span class="qa-hint">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.MULTISELECT_HINT')
                }}</span>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.TEAMS')
                }}</span>
                <select
                  v-model="policyForm.scopeTeamIds"
                  class="qa-select qa-select--multi"
                  multiple
                >
                  <option
                    v-for="team in teamOptions"
                    :key="team.id"
                    :value="Number(team.id)"
                  >
                    {{ team.name || `#${team.id}` }}
                  </option>
                </select>
                <span class="qa-hint">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.MULTISELECT_HINT')
                }}</span>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.CHANNELS')
                }}</span>
                <select
                  v-model="policyForm.scopeChannelTypes"
                  class="qa-select qa-select--multi"
                  multiple
                >
                  <option
                    v-for="channel in channelOptions"
                    :key="channel.value"
                    :value="channel.value"
                  >
                    {{ channel.label }}
                  </option>
                </select>
                <span class="qa-hint">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.MULTISELECT_HINT')
                }}</span>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.NO_REPLY_MINUTES')
                }}</span>
                <input
                  v-model.number="policyForm.noReplyTimeoutMinutes"
                  class="qa-input"
                  data-test="no-reply-minutes"
                  type="number"
                  min="0.1"
                  step="0.5"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.BURST_MINUTES')
                }}</span>
                <input
                  v-model.number="policyForm.customerBurstWindowMinutes"
                  class="qa-input"
                  data-test="burst-minutes"
                  type="number"
                  min="0.1"
                  step="0.5"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.REPLY_MINUTES')
                }}</span>
                <input
                  v-model.number="policyForm.replyTurnWindowMinutes"
                  class="qa-input"
                  data-test="reply-minutes"
                  type="number"
                  min="0.1"
                  step="0.5"
                />
                <span class="qa-hint">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.NO_REPLY_CAP_HINT')
                }}</span>
              </label>
            </div>
          </details>

          <details class="qa-form-section qa-advanced">
            <summary>
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.SECTION_ADVANCED') }}
            </summary>
            <div class="qa-form-grid">
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.PRIORITY')
                }}</span>
                <input
                  v-model.number="policyForm.priority"
                  class="qa-input"
                  type="number"
                  min="0"
                  max="10000"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.ATTACHMENT_MATCH')
                }}</span>
                <select
                  v-model="policyForm.requireAttachmentMatch"
                  class="qa-select"
                >
                  <option :value="false">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.NO') }}
                  </option>
                  <option :value="true">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.YES') }}
                  </option>
                </select>
              </label>
              <label class="qa-field qa-field--span-2">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.ALLOWLIST_PHRASES')
                }}</span>
                <textarea
                  v-model="policyForm.allowlistPhrasesText"
                  class="qa-textarea"
                  rows="2"
                />
              </label>
              <label class="qa-field qa-field--span-2">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.NEGATIVE_TERMS')
                }}</span>
                <textarea
                  v-model="policyForm.negativeTermsText"
                  class="qa-textarea"
                  rows="2"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.TRIGGER_REGEX')
                }}</span>
                <input v-model="policyForm.triggerRegex" class="qa-input" />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.APPROVED_VARIABLES')
                }}</span>
                <input
                  v-model="policyForm.approvedVariablesText"
                  class="qa-input"
                />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.TIMEZONE')
                }}</span>
                <input v-model="policyForm.scheduleTimezone" class="qa-input" />
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.WEEKDAYS')
                }}</span>
                <select
                  v-model="policyForm.scheduleWeekdays"
                  class="qa-select qa-select--multi"
                  multiple
                >
                  <option v-for="day in 7" :key="day - 1" :value="day - 1">
                    {{ t(`QA_DASHBOARD.TEMPLATE_POLICY.WEEKDAY_${day - 1}`) }}
                  </option>
                </select>
              </label>
              <label class="qa-field qa-field--span-2">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.SCHEDULE_WINDOWS')
                }}</span>
                <input
                  v-model="policyForm.scheduleWindowsText"
                  class="qa-input"
                  :placeholder="
                    t('QA_DASHBOARD.TEMPLATE_POLICY.PH_SCHEDULE_WINDOWS')
                  "
                />
              </label>
            </div>
          </details>

          <section class="qa-form-section qa-publish-section">
            <h3>{{ t('QA_DASHBOARD.TEMPLATE_POLICY.SECTION_PUBLISH') }}</h3>
            <div class="qa-lifecycle-note">
              {{
                selectedPolicy
                  ? t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_EXISTING_HINT')
                  : t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_NEW_HINT')
              }}
            </div>
            <div class="qa-form-grid">
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.ENABLE_ON_PUBLISH')
                }}</span>
                <select v-model="policyForm.desiredEnabled" class="qa-select">
                  <option :value="false">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_DISABLED') }}
                  </option>
                  <option :value="true">
                    {{ t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_ENABLED') }}
                  </option>
                </select>
              </label>
              <label class="qa-field">
                <span class="qa-label">{{
                  t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_NOTE')
                }}</span>
                <input
                  v-model="policyForm.changeNote"
                  class="qa-input"
                  :placeholder="
                    t('QA_DASHBOARD.TEMPLATE_POLICY.PH_PUBLISH_NOTE')
                  "
                />
              </label>
            </div>
          </section>
        </div>

        <div v-if="canManagePolicies" class="qa-drawer-footer">
          <div class="qa-actions qa-actions--secondary">
            <button type="button" class="qa-btn" @click="resetPolicyForm">
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.NEW_POLICY') }}
            </button>
            <button
              type="button"
              class="qa-btn qa-btn--danger"
              :disabled="!selectedPolicy || isSaving"
              @click="deletePolicy"
            >
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.DELETE') }}
            </button>
            <button
              type="button"
              class="qa-btn"
              :disabled="!selectedPolicy?.out_of_sync || isSaving"
              @click="syncCannedDraft"
            >
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.SYNC_CANNED') }}
            </button>
            <button
              type="button"
              class="qa-btn"
              :disabled="
                Number(selectedPolicy?.current_version) < 2 || isSaving
              "
              @click="rollbackPolicy"
            >
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.ROLLBACK') }}
            </button>
          </div>
          <div class="qa-actions">
            <button
              type="button"
              class="qa-btn"
              data-test="save-draft"
              :disabled="isSaving"
              @click="savePolicy()"
            >
              {{
                isSaving
                  ? t('QA_DASHBOARD.TEMPLATE_POLICY.BTN_SAVING')
                  : selectedPolicy
                    ? t('QA_DASHBOARD.TEMPLATE_POLICY.BTN_SAVE_DRAFT')
                    : t('QA_DASHBOARD.TEMPLATE_POLICY.BTN_CREATE_DRAFT')
              }}
            </button>
            <button
              type="button"
              class="qa-btn qa-btn--primary"
              data-test="publish-draft"
              :disabled="isSaving || !selectedPolicy"
              @click="publishPolicy"
            >
              {{ t('QA_DASHBOARD.TEMPLATE_POLICY.PUBLISH_DRAFT') }}
            </button>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
@import '../styles/qaShell.css';

.qa-readiness {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 10px;
  background: var(--qa-surface, #fff);
}
.qa-readiness-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.qa-status-dot {
  width: 12px;
  height: 12px;
  margin-top: 4px;
  border-radius: 999px;
  background: #94a3b8;
}
.qa-status-dot--on {
  background: #16a34a;
}
.qa-status-dot--shadow {
  background: #d97706;
}
.qa-status-dot--off {
  background: #dc2626;
}
.qa-readiness-counts {
  display: grid;
  grid-template-columns: repeat(4, minmax(80px, 1fr));
  gap: 16px;
}
.qa-readiness-counts div {
  display: flex;
  flex-direction: column;
}
.qa-readiness-counts strong {
  font-size: 20px;
}
.qa-readiness-counts span,
.qa-policy-state span {
  font-size: 11px;
  color: var(--qa-text-muted, #6b7280);
}
.qa-empty {
  padding: 28px;
  text-align: center;
  color: var(--qa-text-muted, #6b7280);
}
.qa-empty--onboarding h3 {
  margin: 0 0 6px;
  color: var(--qa-text, #111827);
}
.qa-empty--onboarding p {
  margin: 0 0 14px;
}
.qa-policy-list {
  display: grid;
  gap: 10px;
}
.qa-policy-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, auto) auto;
  align-items: center;
  gap: 16px;
  padding: 14px;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 8px;
}
.qa-policy-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.qa-policy-main p {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--qa-text-muted, #6b7280);
}
.qa-policy-state {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}
.qa-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
}
.qa-drawer {
  width: min(760px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  color: var(--qa-text, #111827);
  background: var(--qa-surface, #fff);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
}
.qa-drawer-header,
.qa-drawer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--qa-border, #e5e7eb);
}
.qa-drawer-footer {
  border-top: 1px solid var(--qa-border, #e5e7eb);
  border-bottom: 0;
  background: var(--qa-surface, #fff);
}
.qa-drawer-body {
  flex: 1;
  padding: 0 20px 24px;
  overflow: auto;
}
.qa-policy-recipe {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin: 6px 0 0;
  color: var(--qa-text-muted, #6b7280);
  font-size: 12px;
  line-height: 1.45;
}
.qa-policy-recipe em {
  font-style: normal;
  font-weight: 600;
  color: var(--qa-text, #111827);
}
.qa-policy-recipe-arrow {
  color: var(--qa-text-muted, #9ca3af);
}
.qa-recipe-strip {
  display: grid;
  grid-template-columns: 1fr auto 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 8px;
  background: var(--qa-muted, #f8fafc);
}
.qa-recipe-strip .qa-label {
  display: block;
  margin-bottom: 4px;
}
.qa-recipe-strip strong {
  font-size: 13px;
  word-break: break-word;
}
@media (max-width: 760px) {
  .qa-recipe-strip {
    grid-template-columns: 1fr;
  }
  .qa-policy-recipe-arrow {
    display: none;
  }
}
.qa-form-section {
  padding: 18px 0;
  border-bottom: 1px solid var(--qa-border, #e5e7eb);
}
.qa-form-section h3 {
  margin: 0 0 12px;
  font-size: 14px;
}
.qa-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.qa-field--span-2 {
  grid-column: span 2;
}
.qa-select--multi {
  min-height: 84px;
}
.qa-section-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.qa-section-heading h3 {
  margin-bottom: 2px;
}
.qa-canned-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0;
}
.qa-chip {
  padding: 5px 10px;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 999px;
  background: var(--qa-muted, #f8fafc);
  cursor: pointer;
}
.qa-chip--active {
  border-color: #0f766e;
  background: #ecfdf5;
}
.qa-chip--warn {
  border-color: #b45309;
  background: #fffbeb;
}
.qa-chip-x {
  margin-left: 6px;
}
.qa-canned-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  gap: 10px;
  margin-top: 10px;
}
.qa-canned-picker {
  max-height: 250px;
  overflow: auto;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 8px;
}
.qa-canned-option {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 3px;
  padding: 9px 10px;
  border: 0;
  border-bottom: 1px solid var(--qa-border, #e5e7eb);
  color: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;
}
.qa-canned-option span,
.qa-canned-option small {
  overflow: hidden;
  color: var(--qa-text-muted, #6b7280);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qa-canned-option--selected {
  background: #ecfdf5;
}
.qa-canned-empty {
  padding: 14px;
  color: var(--qa-text-muted, #6b7280);
}
.qa-canned-preview {
  min-height: 120px;
  padding: 10px;
  border: 1px solid var(--qa-border, #e5e7eb);
  border-radius: 8px;
  background: var(--qa-muted, #f8fafc);
}
.qa-canned-preview pre {
  margin: 8px 0 0;
  font: inherit;
  font-size: 12px;
  white-space: pre-wrap;
}
.qa-advanced summary {
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.qa-advanced[open] summary {
  margin-bottom: 14px;
}
.qa-lifecycle-note {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #92400e;
  background: #fffbeb;
  font-size: 12px;
}
.qa-checkline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--qa-text-muted, #6b7280);
}
.qa-actions--secondary {
  flex: 1;
}
@media (max-width: 760px) {
  .qa-readiness,
  .qa-drawer-footer {
    align-items: stretch;
    flex-direction: column;
  }
  .qa-readiness-counts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .qa-policy-row {
    grid-template-columns: 1fr;
  }
  .qa-form-grid,
  .qa-canned-grid {
    grid-template-columns: 1fr;
  }
  .qa-field--span-2 {
    grid-column: auto;
  }
}
</style>
